import { addNotification } from 'components/Notifications'
import { RootState } from 'data/reducer'
import { AppEvent, logAppEvent } from 'lib/analytics'
import { i18n } from 'lib/i18n'
import { deserializePlaceV0, serializeCompactV0 } from 'lib/place'
import { isHereId } from 'lib/place/util'
import { AnyAction } from 'redux'
import { Epic, StateObservable } from 'redux-observable'
import {
  catchError,
  concat,
  filter,
  from,
  Observable,
  of,
  switchMap,
  toArray,
  withLatestFrom,
} from 'rxjs'
import { AjaxError } from 'rxjs/ajax'

import {
  DataItem,
  httpCreateCollection,
  httpDeleteCollection,
  httpEditCollection,
} from '../api/collections'
import {
  DataItem as ScbePlaceResponseData,
  FavoritePlaceBody,
  httpAssignPlaceToCollection,
  httpCreateFavoritePlace,
  httpDeletePlaceFromCollection,
} from '../api/places'
import { mapToCollection, mapToPlace, mapToPlaceV0, Place, Status } from '../model'
import {
  createCollection,
  deleteCollection,
  deletePlaceFromCollection,
  editCollection,
  fetchCollectionsAndPlaces,
  savePlaceInCollection,
  setCollections,
  setError,
  setLoadingStatus,
  setPaginationToken as setPageToken,
  setPlaces,
} from '../reducer/reducer'
import {
  CollectionEntry,
  createCollectionEntries$,
  createCollectionPlaceEntries$,
  deserializePlaceId,
  EntriesPayload,
  isReFetchingTheFirstPage,
  removeSerializationVersion,
} from './util/fetchCollectionsAndPlacesEpic'

export const fetchCollectionsAndPlacesEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(fetchCollectionsAndPlaces.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      void logAppEvent(AppEvent.CollectionsReadRequested)
      // Prevent to re-fetch the first page if the collections are already loaded
      if (isReFetchingTheFirstPage(action.payload.pageToken, state.collections.collectionMap)) {
        return of(setLoadingStatus(Status.fetchCollectionsAndPlacesSuccess))
      }

      return createCollectionEntries$(
        state.account.user?.accessToken ?? '',
        state.account.user?.sub ?? '',
        action.payload.pageToken
      ).pipe(
        switchMap((collectionEntriesPayload: EntriesPayload<CollectionEntry>) => {
          void logAppEvent(AppEvent.CollectionsReadSucceeded, {
            count: collectionEntriesPayload.entries.length,
          })
          const auxData = collectionEntriesPayload.data
          const collectionEntries = collectionEntriesPayload.entries
          const lang = state.app.language
          const placeEntries$ =
            collectionEntries.length > 0
              ? collectionEntries.map(([id, _]) =>
                  createCollectionPlaceEntries$(id, state.account.user?.accessToken ?? '', lang)
                )
              : [of([])]

          void logAppEvent(AppEvent.CollectedPlacesReadRequested)
          return concat(...placeEntries$).pipe(
            toArray(),
            switchMap((places) => {
              void logAppEvent(AppEvent.CollectedPlacesReadSucceeded, {
                count: places.length,
              })
              return from([
                setPlaces({
                  ...state.collections.placeMap,
                  ...Object.fromEntries([...places.flat()]),
                }),
                setCollections({
                  ...state.collections.collectionMap,
                  ...Object.fromEntries(collectionEntries),
                }),
                setPageToken(auxData.pageToken),
                setLoadingStatus(Status.fetchCollectionsAndPlacesSuccess),
              ])
            }),
            catchError((err: AjaxError) => {
              void logAppEvent(AppEvent.CollectedPlacesReadFailed, { error: err.message })
              return of(
                setError(`Unable to fetch places, reason: ${err.message}`),
                setLoadingStatus(Status.fetchCollectionsAndPlacesError)
              )
            })
          )
        }),
        catchError((err: AjaxError) => {
          void logAppEvent(AppEvent.CollectionsReadFailed, { error: err.message })
          return of(
            setError(`Unable to fetch collections , reason: ${err.message}`),
            setLoadingStatus(Status.fetchCollectionsAndPlacesError)
          )
        })
      )
    })
  )

export const createCollectionEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(createCollection.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      return httpCreateCollection(
        state.account.user?.accessToken ?? '',
        state.account.user?.sub ?? '',
        action.payload.collection
      ).pipe(
        switchMap((responseData: DataItem) => {
          const newEntries: CollectionEntry[] = [[responseData.id, mapToCollection(responseData)]]

          void logAppEvent(AppEvent.CollectionCreated, { result: 'success' })

          return from([
            setCollections({
              ...Object.fromEntries(newEntries),
              ...state.collections.collectionMap,
            }),
            setLoadingStatus(Status.createCollectionSuccess),
          ])
        }),
        catchError((err: AjaxError) => {
          void logAppEvent(AppEvent.CollectionCreated, { error: err.message, result: 'fail' })
          return of(
            setError(`Unable to create new collection, reason: ${err.message}`),
            setLoadingStatus(Status.createCollectionError)
          )
        })
      )
    })
  )

export const editCollectionEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(editCollection.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      return httpEditCollection(
        state.account.user?.accessToken ?? '',
        state.account.user?.sub ?? '',
        action.payload.collection
      ).pipe(
        switchMap((responseData: DataItem) => {
          const newEntries: CollectionEntry[] = [[responseData.id, mapToCollection(responseData)]]
          const collectionMap = { ...state.collections.collectionMap }
          delete collectionMap[responseData.id]

          void logAppEvent(AppEvent.CollectionModified, { result: 'success' })

          return from([
            setCollections({
              ...Object.fromEntries(newEntries),
              ...collectionMap,
            }),
            setLoadingStatus(Status.editCollectionSuccess),
          ])
        }),
        catchError((err: AjaxError) => {
          void logAppEvent(AppEvent.CollectionModified, { error: err.message, result: 'fail' })
          return of(
            setError(`Unable to edit collection, reason: ${err.message}`),
            setLoadingStatus(Status.editCollectionError)
          )
        })
      )
    })
  )

export const deleteCollectionEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(deleteCollection.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      return httpDeleteCollection(
        state.account.user?.accessToken ?? '',
        state.account.user?.sub ?? '',
        action.payload.collectionId
      ).pipe(
        switchMap(() => {
          const collectionMap = { ...state.collections.collectionMap }
          delete collectionMap[action.payload.collectionId]

          void logAppEvent(AppEvent.CollectionDeleted, { result: 'success' })

          return from([
            setCollections({
              ...collectionMap,
            }),
            setLoadingStatus(Status.deleteCollectionSuccess),
          ])
        }),
        catchError((err: AjaxError) => {
          void logAppEvent(AppEvent.CollectionDeleted, { result: 'fail' })

          return of(
            setError(`Unable to delete collection, reason: ${err.message}`),
            setLoadingStatus(Status.deleteCollectionError)
          )
        })
      )
    })
  )

export const savePlaceInCollectionEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(savePlaceInCollection.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      if (
        state.account === undefined ||
        state.account.user === undefined ||
        state.account.user.accessToken === undefined
      ) {
        return of(setLoadingStatus(Status.savePlaceInCollectionError))
      }

      const accessToken = state.account.user.accessToken
      const pdcItem = action.payload.pdcItem
      const placeAlreadySaved = Object.values(state.collections.placeMap).find(
        (place) => place.placeId === pdcItem.id || place.placesForeignId === pdcItem.id
      )

      const favoritePlaceBody: FavoritePlaceBody = {
        customName: pdcItem.title,
        location: {
          address: {
            text: pdcItem.addressLabel !== undefined ? pdcItem.addressLabel : '',
          },
          position: {
            latitude: pdcItem.position !== undefined ? pdcItem.position.lat : 0,
            longitude: pdcItem.position !== undefined ? pdcItem.position.lng : 0,
          },
        },
        name: pdcItem.title,
        placesForeignId: pdcItem.id !== undefined ? pdcItem.id : '',
      }

      const collectionName = state.collections.collectionMap[action.payload.collectionId].name

      if (placeAlreadySaved === undefined) {
        return httpCreateFavoritePlace(accessToken, favoritePlaceBody).pipe(
          switchMap((responseData) => {
            return httpAssignPlaceToCollection(
              accessToken,
              action.payload.collectionId,
              responseData.id
            ).pipe(
              switchMap((response) => {
                const places = { ...state.collections.placeMap }
                const place = mapResponseToPlace(response)
                places[place.id] = place
                return from([
                  setPlaces(places),
                  addNotification({
                    content: i18n('collections.actions.notifications.add', [collectionName]),
                    inline: true,
                    variant: 'confirmation',
                  }),
                  setLoadingStatus(Status.savePlaceInCollectionSuccess),
                ])
              }),
              catchError((err: AjaxError) => {
                return of(
                  setError(`Unable to assign favorite place to collection, reason: ${err.message}`),
                  setLoadingStatus(Status.savePlaceInCollectionError)
                )
              })
            )
          }),
          catchError((err: AjaxError) => {
            return of(
              setError(`Unable to create favorite place, reason: ${err.message}`),
              setLoadingStatus(Status.savePlaceInCollectionError)
            )
          })
        )
      }

      return httpAssignPlaceToCollection(
        accessToken,
        action.payload.collectionId,
        placeAlreadySaved.id
      ).pipe(
        switchMap((response) => {
          const places = { ...state.collections.placeMap }
          const place = mapResponseToPlace(response)
          places[place.id] = place
          return from([
            setPlaces(places),
            addNotification({
              content: i18n('collections.actions.notifications.add', [collectionName]),
              inline: true,
              variant: 'confirmation',
            }),
          ])
        }),
        catchError((err: AjaxError) => {
          return of(
            setError(`Unable to assign favorite place to collection, reason: ${err.message}`),
            setLoadingStatus(Status.savePlaceInCollectionError)
          )
        })
      )
    })
  )

export const deletePlaceFromCollectionEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(deletePlaceFromCollection.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      let scbePlaceId = ''
      const placeMap = { ...state.collections.placeMap }
      const collectionName = state.collections.collectionMap[action.payload.collectionId].name

      Object.values(placeMap).find((place: Place) => {
        const deserializedPlace = deserializePlaceV0(place.idV0)

        if (
          (deserializedPlace.id === action.payload.placeId ||
            deserializedPlace.placesForeignId === action.payload.placeId) &&
          place.collectionId.includes(action.payload.collectionId)
        ) {
          scbePlaceId = place.id
        }
      })

      if (scbePlaceId !== '') {
        return httpDeletePlaceFromCollection(
          state.account.user?.accessToken ?? '',
          scbePlaceId,
          action.payload.collectionId
        ).pipe(
          switchMap(() => {
            const place = { ...placeMap[scbePlaceId] }

            place.collectionId = place.collectionId.filter(
              (collectionId) => collectionId !== action.payload.collectionId
            )

            placeMap[scbePlaceId] = place

            if (place.collectionId.length === 0) {
              delete placeMap[scbePlaceId]
            }

            return from([
              setPlaces({
                ...placeMap,
              }),
              addNotification({
                content: i18n('collections.actions.notifications.remove', [collectionName]),
                icon: 'delete',
                inline: true,
                variant: 'confirmation',
              }),
              setLoadingStatus(Status.deletePlaceInCollectionSuccess),
            ])
          }),
          catchError((err: AjaxError) =>
            of(
              setError(`Unable to delete place from collection, reason: ${err.message}`),
              setLoadingStatus(Status.deletePlaceInCollectionError)
            )
          )
        )
      }
      return from([
        setError(`Unable to delete place from collection, reason: Missing ID`),
        setLoadingStatus(Status.deletePlaceInCollectionError),
      ])
    })
  )

const mapResponseToPlace = (place: ScbePlaceResponseData): Place => {
  const deserializedData =
    place.placesId !== undefined && !isHereId(place.placesId)
      ? deserializePlaceId(place.placesId)
      : undefined

  const intrinsicId =
    deserializedData !== undefined
      ? deserializedData.id
      : place.placesId !== undefined && isHereId(place.placesId)
      ? place.placesId
      : place.placesForeignId !== undefined && isHereId(place.placesForeignId)
      ? place.placesForeignId
      : undefined

  const serializedPlaceV0: string =
    place.placesId !== undefined && deserializedData !== undefined
      ? removeSerializationVersion(place.placesId)
      : serializeCompactV0(mapToPlaceV0(place, intrinsicId))

  return mapToPlace(place, serializedPlaceV0, intrinsicId, deserializedData)
}
